/**
 * 瓦片服务 —— 把"缓存存哪儿""现在能不能联网""密钥从哪读"这几件事收成一个对象，
 * 交给 `tileProtocol` 使用，并注册 `hgc://` 协议。
 *
 * ## 为什么要有这一层
 *
 * `tileProtocol.handleTileRequest` 刻意是纯的（依赖全从参数进来），`idbTileStore`
 * 刻意只会读写。把两者接起来需要有人回答三个问题：IndexedDB 能不能用（不能用就内存兜底）、
 * 现在是 `online` 还是 `cache-only`、某个源的密钥是什么。答案只能有一份 ——
 * 两个地方各自去 `indexedDB.open` 会得到两个不同的缓存视图，于是"覆盖判定说已缓存、
 * 实际取图却缺"。
 *
 * ## 只有一种"离线"是不可信的：猜出来的那种
 *
 * `online` 模式下断网不会让地图坏掉：取图失败会在协议层退化成"占位瓦片 + 记一条事件"，
 * 所以**不需要**用 `navigator.onLine` 去猜然后自动切模式（那个值在野外经常是错的，
 * 而"猜错了就静默只看缓存"会让用户以为瓦片服务挂了）。`cache-only` 只由用户显式打开。
 *
 * ## 内存兜底要如实说出来
 *
 * 隐私模式、被策略禁用的存储、配额耗尽都会让 IndexedDB 打不开。这时地图照常工作，
 * 但**刷新页面缓存就没了** —— 这件事实必须显示在界面上（`durabilityNote`），
 * 否则用户会以为"我已经把这片区域缓存好了"，直到野外发现什么都没有。
 */

import { useEffect, useState } from 'react';

import { openTileDatabase, type OpenTileDatabaseResult } from './idbTileStore';
import { defaultSourceCatalog, readSourceKey } from './sources';
import {
  createMemoryPersistence,
  createTileStore,
  type TileCacheStats,
  type TileStore,
} from './tileStore';
import {
  handleTileRequest,
  type MapResourceMode,
  type TileEvent,
  type TileProtocolDeps,
} from './tileProtocol';
import { HGC_TILE_SCHEME } from './tiles';

export type TileCacheBackend = 'indexeddb' | 'memory';

/** 事件缓冲区大小。界面只显示一个汇总，留最近若干条足够，也不至于把内存吃满。 */
export const TILE_EVENT_LOG_LIMIT = 200;

export interface TileService {
  readonly store: TileStore;
  readonly backend: TileCacheBackend;
  /** 内存兜底时的中文原因；持久可用时为 `null` */
  readonly durabilityNote: string | null;
  mode(): MapResourceMode;
  setMode(mode: MapResourceMode): void;
  /** 记录一条瓦片事件（由 `tileProtocolDeps` 调用） */
  record(event: TileEvent): void;
  /** 最近若干条瓦片事件（新 → 旧） */
  recentEvents(): readonly TileEvent[];
  onEvent(listener: (event: TileEvent) => void): () => void;
  stats(): Promise<TileCacheStats>;
  /** 清空缓存（可只清某个源） */
  clear(sourceId?: string): Promise<void>;
}

export interface TileServiceOptions {
  /** 注入数据库打开方式（测试用）；默认走 `openTileDatabase` */
  readonly openDatabase?: () => Promise<OpenTileDatabaseResult>;
  readonly mode?: MapResourceMode;
  readonly now?: () => number;
}

export async function createTileService(options: TileServiceOptions = {}): Promise<TileService> {
  const opened = await (options.openDatabase ?? openTileDatabase)();
  const backend: TileCacheBackend = opened.persistence === null ? 'memory' : 'indexeddb';
  const store = createTileStore({
    persistence: opened.persistence ?? createMemoryPersistence(),
    ...(options.now === undefined ? {} : { now: options.now }),
  });

  let mode: MapResourceMode = options.mode ?? 'online';
  let events: readonly TileEvent[] = [];
  const listeners = new Set<(event: TileEvent) => void>();

  return {
    store,
    backend,
    durabilityNote:
      backend === 'indexeddb'
        ? null
        : `地图缓存退化为内存模式（${opened.reason ?? '浏览器未提供持久存储'}），刷新页面后已缓存的瓦片会丢失。`,
    mode: () => mode,
    setMode: (next) => {
      mode = next;
    },
    record: (event) => {
      // 新 → 旧，超出上限丢最旧的
      events = [event, ...events].slice(0, TILE_EVENT_LOG_LIMIT);
      for (const listener of [...listeners]) listener(event);
    },
    recentEvents: () => events,
    onEvent: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    stats: () => store.stats(),
    clear: (sourceId) => store.clear(sourceId),
  };
}

/** `tileProtocol` 需要的依赖。密钥从 `localStorage` 读（见 `sources.ts`）。 */
export function tileProtocolDeps(
  service: TileService,
  extra: { readonly pixelRatio?: number } = {},
): TileProtocolDeps {
  return {
    store: service.store,
    source: (sourceId) => defaultSourceCatalog.get(sourceId),
    key: (sourceId) => readSourceKey(sourceId),
    mode: () => service.mode(),
    onEvent: (event) => service.record(event),
    ...(extra.pixelRatio === undefined ? {} : { pixelRatio: extra.pixelRatio }),
  };
}

/**
 * 协议处理器签名（与 MapLibre 的 `addProtocol` 兼容）。
 *
 * 自己声明这个类型而不是引 MapLibre 的类型：本模块要能在没有 DOM/WebGL 的环境里被测试，
 * 而 `addProtocol` 的实现由调用方注入（`MapCanvas` 传真的，测试传假的）。
 */
export type TileRequestHandler = (
  params: { readonly url: string },
  controller: AbortController,
) => Promise<{ readonly data: ArrayBuffer }>;

export type AddProtocolFn = (scheme: string, handler: TileRequestHandler) => void;

let protocolRegistered = false;

/**
 * 注册 `hgc://` 协议。**幂等**：MapLibre 对同一个协议重复注册会抛错，
 * 而组件重新挂载（切换分区再切回来）一定会再调一次。
 *
 * 返回值表示"这次是不是真的注册了"，测试用它断言幂等。
 */
export function registerTileProtocol(service: TileService, addProtocol: AddProtocolFn): boolean {
  if (protocolRegistered) return false;
  addProtocol(HGC_TILE_SCHEME, async (params, controller) => ({
    data: await handleTileRequest(tileProtocolDeps(service), {
      url: params.url,
      signal: controller.signal,
    }),
  }));
  protocolRegistered = true;
  return true;
}

/** 测试专用：允许下一个用例重新注册。 */
export function resetTileProtocolRegistration(): void {
  protocolRegistered = false;
}

let servicePromise: Promise<TileService> | null = null;

/**
 * 全应用一份的瓦片服务。
 *
 * 单例而不是每个地图一份：缓存是 IndexedDB 里的一份共享数据，两个服务实例会各自
 * 维护一份元数据索引，于是 LRU 淘汰会按两半的视角各删一次。
 */
export function getTileService(): Promise<TileService> {
  servicePromise ??= createTileService();
  return servicePromise;
}

/** 测试专用：丢弃单例（调用方负责不再使用旧实例）。 */
export function resetTileService(): void {
  servicePromise = null;
  protocolRegistered = false;
}

/**
 * 在 React 里拿瓦片服务。
 *
 * 返回 `null` 表示"还没打开好" —— 界面必须处理这一帧（按钮禁用并说明原因），
 * 而不是先假设它已经就绪。打开失败不会抛：服务本身一定会建成（最差是内存兜底）。
 */
export function useTileService(): TileService | null {
  const [service, setService] = useState<TileService | null>(null);
  useEffect(() => {
    let alive = true;
    void getTileService().then((created) => {
      if (alive) setService(created);
    });
    return () => {
      alive = false;
    };
  }, []);
  return service;
}
